/**
 * Normalizes AttendancePolicyComponent / shift form fields for the calculation engine.
 */

export const PENALTY_TYPES = ["none", "minutes_lop", "half_day", "full_day", "lop", "absent"];

/** Half-day composites (first half / second half) — no separate HD code. */
export const HALF_DAY_LOP_PR = "LOP/PR";
export const HALF_DAY_PR_LOP = "PR/LOP";
/** Late first half, second half not earned yet (open session or short day). */
export const HALF_DAY_LOP_OPEN = "LOP/-";
/** First half present, second half still in progress. */
export const HALF_DAY_PR_OPEN = "PR/-";

/** First-half / second-half label for live punch + calendar (e.g. LOP/- → LP | -). */
export function formatCompositeStatusLabel(status, mapping = {}) {
  const raw = String(status || "").trim();
  if (!raw || raw === "-") return raw || "-";
  const mapPart = (part) => {
    const t = String(part || "").trim();
    if (!t) return "-";
    const u = t.toUpperCase();
    return mapping[u] || t;
  };
  if (raw.includes("/")) {
    const [a, b] = raw.split("/");
    return `${mapPart(a)} | ${mapPart(b)}`;
  }
  return mapPart(raw);
}

/** Map UI latePenaltyType → engine action + day status impact */
export function normalizePenaltyType(raw) {
  const t = String(raw || "none").toLowerCase();
  if (PENALTY_TYPES.includes(t)) return t;
  if (t === "lop") return "full_day";
  return "none";
}

/** Sync legacy latePenaltyAction from UI latePenaltyType on save/load */
export function normalizePolicyRecord(policy) {
  if (!policy || typeof policy !== "object") return policy;
  const lateType = normalizePenaltyType(
    policy.latePenaltyType || policy.latePenaltyAction
  );
  const earlyType = normalizePenaltyType(
    policy.earlyDeparturePenaltyType || policy.earlyExitPenaltyAction
  );
  // Manual override: Auto attendance + Override Attendance.
  // Migrate legacy processMode === "manual".
  const legacyManual = policy.processMode === "manual";
  const autoAttendance = !!(policy.autoAttendance || legacyManual);
  const overrideAttendance = !!(
    policy.overrideAttendance ||
    (legacyManual && autoAttendance)
  );
  const isManualOverride = !!(autoAttendance && overrideAttendance);
  // When Override Attendance is on, punch/capture fields are not in force — keep them off
  // so preview/document/engine never treat leftover Auto settings as active.
  const punchCleared = isManualOverride
    ? {
        checkInMobile: false,
        checkInWeb: false,
        checkInBiometric: false,
        checkInManual: false,
        trackLocation: false,
        restrictLocationForOnlineCheckIn: false,
        enableGeoFencing: false,
        enforceIpRestriction: false,
        enforceDeviceBinding: false,
        allowedIpRanges: "",
        autoLogoutEnabled: false,
      }
    : {};
  return {
    ...policy,
    ...punchCleared,
    autoAttendance,
    overrideAttendance: autoAttendance ? overrideAttendance : false,
    processMode: isManualOverride ? "manual" : "auto",
    // Attendance cut-off (LOP deferral) — applies to Auto attendance / Override / punch policies
    cutOffEnabled: !!(
      policy.cutOffEnabled === true ||
      (String(policy.cutOffMethod || "") === "specific" &&
        Number(policy.cutOffToDay || policy.cutOffDay) > 0)
    ),
    cutOffDay: (() => {
      const n = Number(policy.cutOffDay ?? policy.cutOffToDay ?? 25);
      if (!Number.isFinite(n)) return 25;
      return Math.min(28, Math.max(1, Math.floor(n)));
    })(),
    cutOffNewJoinerHandling: ["defer_lop", "block_joining", "no_adjustment"].includes(
      policy.cutOffNewJoinerHandling
    )
      ? policy.cutOffNewJoinerHandling
      : "defer_lop",
    cutOffSeparationHandling: [
      "fnf_recovery",
      "current_month_deduct",
      "no_recovery",
    ].includes(policy.cutOffSeparationHandling)
      ? policy.cutOffSeparationHandling
      : "fnf_recovery",
    cutOffExcessPayRecovery: policy.cutOffExcessPayRecovery !== false,
    employeeStatusEditEnabled: !!(
      autoAttendance && policy.employeeStatusEditEnabled
    ),
    employeeStatusEditRequiresWorkflow:
      policy.employeeStatusEditRequiresWorkflow !== false,
    leaveAttendancePriorityRule: ["attendance_first", "hybrid", "approved_leave_first"].includes(
      String(policy.leaveAttendancePriorityRule || "").trim()
    )
      ? String(policy.leaveAttendancePriorityRule).trim()
      : "approved_leave_first",
    latePenaltyType: lateType,
    earlyDeparturePenaltyType: earlyType,
    latePenaltyAction:
      lateType === "half_day" || lateType === "full_day" || lateType === "minutes_lop"
        ? "lop"
        : lateType === "absent"
        ? "absent"
        : policy.latePenaltyAction || "none",
    earlyExitPenaltyAction:
      earlyType === "half_day" || earlyType === "full_day" || earlyType === "minutes_lop"
        ? "lop"
        : earlyType === "absent"
        ? "absent"
        : policy.earlyExitPenaltyAction || "none",
  };
}

/** True when policy uses Manual marking (Change Status / default Present / no punches). */
export function isPolicyOverrideAttendance(policy) {
  if (!policy) return false;
  const n = normalizePolicyRecord(policy);
  return !!(n.autoAttendance && n.overrideAttendance);
}

export function normalizeShiftRecord(shift) {
  if (!shift || typeof shift !== "object") return shift;
  return {
    ...shift,
    shiftType: shift.shiftType || (shift.flexiEnabled ? "flexi" : "fixed"),
    latePenaltyType: normalizePenaltyType(shift.latePenaltyType),
    earlyDeparturePenaltyType: normalizePenaltyType(shift.earlyDeparturePenaltyType),
  };
}

/**
 * Ownership (P1):
 * - Shift = clock template (start/end, flexi, breaks, cross-day, override).
 * - Policy = day marks, grace rules, penalties, missing punch, regularization.
 * - Hours thresholds from Policy, except when the employee is on a different
 *   assigned shift (Change Shift) that carries its own full-day hours.
 * - Grace minutes from Policy unless shift.allowGraceOverride is true.
 */
export function getEffectiveTimingConfig(policy = {}, shift = null) {
  const p = normalizePolicyRecord(policy || {});
  const s = shift ? normalizeShiftRecord(shift) : null;
  const shiftGraceOverride = !!(s?.allowGraceOverride);
  const policyShiftId = String(p.shiftGroupId || "").trim().toLowerCase();
  const assignedId = String(s?.code || s?.id || "").trim().toLowerCase();
  const useShiftHours =
    !!s &&
    !!assignedId &&
    !!policyShiftId &&
    assignedId !== policyShiftId &&
    (s.fullDayIfHoursAtLeast != null || s.fullDayHours != null);
  const useShiftPenalty =
    !!s &&
    !!assignedId &&
    !!policyShiftId &&
    assignedId !== policyShiftId &&
    s.latePenaltyType != null &&
    String(s.latePenaltyType).trim() !== "";
  const fullDayIfHoursAtLeast = useShiftHours
    ? Number(s.fullDayIfHoursAtLeast ?? s.fullDayHours ?? p.fullDayIfHoursAtLeast ?? 8)
    : Number(p.fullDayIfHoursAtLeast ?? p.fullDayHours ?? 8);
  const halfDayMarkIfHoursBelow = useShiftHours
    ? Number(
        s.halfDayMarkIfHoursBelow ??
          s.fullDayIfHoursAtLeast ??
          s.fullDayHours ??
          p.halfDayMarkIfHoursBelow ??
          4
      )
    : Number(p.halfDayMarkIfHoursBelow ?? p.halfDayIfHoursLessThan ?? p.halfDayHours ?? 4);

  const pickGrace = (key, fallback) => {
    if (shiftGraceOverride && s && s[key] != null && s[key] !== "") return s[key];
    if (p[key] != null && p[key] !== "") return p[key];
    return fallback;
  };

  return {
    graceRulesEnabled: p.graceRulesEnabled !== false,
    graceCheckInMinutes: Number(pickGrace("graceCheckInMinutes", 15)),
    graceCheckOutMinutes: Number(pickGrace("graceCheckOutMinutes", 15)),
    considerLateAfterMinutes: Number(pickGrace("considerLateAfterMinutes", 15)),
    considerEarlyBeforeMinutes: Number(pickGrace("considerEarlyBeforeMinutes", 30)),
    earlyLoginBufferMinutes: Number(pickGrace("earlyLoginBufferMinutes", 0)),
    lateLogoutBufferMinutes: Number(pickGrace("lateLogoutBufferMinutes", 0)),
    graceOccurrenceWindow: p.graceOccurrenceWindow || "monthly",
    lateGraceAllowedOccurrences: Number(
      p.lateGraceAllowedOccurrences ?? p.lateAllowedOccurrencesPerMonth ?? 0
    ),
    earlyGraceAllowedOccurrences: Number(
      p.earlyGraceAllowedOccurrences ?? p.earlyExitAllowedOccurrencesPerMonth ?? 0
    ),
    lateAllowedOccurrencesPerMonth: Number(
      p.lateAllowedOccurrencesPerMonth ?? p.lateGraceAllowedOccurrences ?? 0
    ),
    earlyExitAllowedOccurrencesPerMonth: Number(
      p.earlyExitAllowedOccurrencesPerMonth ?? p.earlyGraceAllowedOccurrences ?? 0
    ),
    // Penalties: policy shift uses Attendance Policy. A different assigned shift
    // (Change Shift) keeps its own latePenaltyType so General half-day late
    // does not apply to Afternoon Flexi.
    latePenaltyType: useShiftPenalty
      ? normalizePenaltyType(s.latePenaltyType)
      : normalizePenaltyType(p.latePenaltyType || p.latePenaltyAction),
    latePenaltyMinutes: Number(
      useShiftPenalty ? s.latePenaltyMinutes ?? 0 : p.latePenaltyMinutes ?? 0
    ),
    earlyDeparturePenaltyType: normalizePenaltyType(
      useShiftPenalty
        ? s.earlyDeparturePenaltyType || s.earlyExitPenaltyAction || "none"
        : p.earlyDeparturePenaltyType || p.earlyExitPenaltyAction
    ),
    earlyDeparturePenaltyMinutes: Number(p.earlyDeparturePenaltyMinutes ?? 0),
    // Hours thresholds: Policy, unless a non-policy assigned shift defines its own
    fullDayHours: Number(
      useShiftHours
        ? s.fullDayHours ?? s.fullDayIfHoursAtLeast ?? p.fullDayHours ?? p.fullDayIfHoursAtLeast ?? 8
        : p.fullDayHours ?? p.fullDayIfHoursAtLeast ?? 8
    ),
    halfDayHours: Number(p.halfDayHours ?? p.halfDayIfHoursLessThan ?? 4),
    fullDayIfHoursAtLeast,
    halfDayIfHoursLessThan: Number(p.halfDayIfHoursLessThan ?? p.halfDayHours ?? 4),
    halfDayMarkIfHoursBelow,
    absentMarkIfHoursBelow: Number(p.absentMarkIfHoursBelow ?? 2),
    autoAbsentMarkingEnabled: p.autoAbsentMarkingEnabled !== false,
    autoHalfDayMarkingEnabled: p.autoHalfDayMarkingEnabled !== false,
    defaultMarkingForMissingPunch: p.defaultMarkingForMissingPunch || "half_day",
    breakDeductionMethod: p.breakDeductionMethod || "fixed",
    breakFixedMinutes: Number(p.breakFixedMinutes ?? 0),
    workingHoursRoundingUnit: Number(p.workingHoursRoundingUnit) || 0,
    workingHoursRoundingDirection: p.workingHoursRoundingDirection || "nearest",
    allowShiftOverride: !!(s?.allowShiftOverride),
    shiftOverrideRequiresApproval: !!(s?.shiftOverrideRequiresApproval),
    checkInBiometric: p.checkInBiometric !== false,
    checkInMobile: p.checkInMobile !== false,
    checkInWeb: p.checkInWeb !== false,
    regularizationAllowed: p.regularizationAllowed !== false,
    shortLeaveEnabled: !!p.shortLeaveEnabled,
    changeShiftEnabled: p.changeShiftEnabled !== false,
    shortLeaveMaxMinutes: Number(
      p.shortLeaveMaxMinutes ?? p.considerLateAfterMinutes ?? 60
    ),
    odRequestEnabled: p.odRequestEnabled !== false,
    overtimeEnabled: !!p.overtimeEnabled,
    compensatoryOffEnabled: !!p.compensatoryOffEnabled,
    otCompOffMode: p.otCompOffMode || "none",
    leaveAttendancePriorityRule: ["attendance_first", "hybrid", "approved_leave_first"].includes(
      String(p.leaveAttendancePriorityRule || "").trim()
    )
      ? String(p.leaveAttendancePriorityRule).trim()
      : "approved_leave_first",
    punchDayMode: ["auto", "shift_window", "calendar_date"].includes(p.punchDayMode)
      ? p.punchDayMode
      : "auto",
    minHoursBetweenShifts: Number(p.minHoursBetweenShifts ?? 8),
    dayEndCheckHours: Number(p.dayEndCheckHours ?? 8),
    overnightOutAttachesToInDay: p.overnightOutAttachesToInDay !== false,
    missingAttendanceAction: p.missingAttendanceAction || "lop",
    compOffCreditMode: p.compOffCreditMode === "prior" ? "prior" : "post",
    overtimeAutoApproveToPayroll: !!p.overtimeAutoApproveToPayroll,
    activityWorkflows: p.activityWorkflows || null,
    shiftType: s?.shiftType || "fixed",
    flexiBandwidthStart: s?.flexiBandwidthStart,
    flexiBandwidthEnd: s?.flexiBandwidthEnd,
    coreHoursStart: s?.coreHoursStart,
    coreHoursEnd: s?.coreHoursEnd,
    crossDayShift: !!(s?.crossDayShift),
  };
}

/**
 * @param {"late"|"early"} context — late/missing → LOP/PR; early exit → PR/LOP
 */
export function penaltyToDayStatus(penaltyType, context = "late") {
  const t = normalizePenaltyType(penaltyType);
  if (t === "full_day" || t === "absent" || t === "lop") return "LOP";
  if (t === "half_day") {
    return context === "early" ? HALF_DAY_PR_LOP : HALF_DAY_LOP_PR;
  }
  return null;
}

export function missingPunchMarkToStatus(mark) {
  const m = String(mark || "half_day").toLowerCase();
  if (m === "present") return "PR";
  if (m === "half_day") return HALF_DAY_LOP_PR;
  return "LOP";
}
